import { useContext, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";

import { TokenContext } from "src/providers/token-provider";
import { useAnalytics } from "src/hooks/useAnalytics";
import { usePassThroughKeys } from "src/hooks/usePassThroughKeys";
import { useTrackPageView } from "src/hooks/useTrackPageView";

/**
 * Business pages
 */
import BusinessUsers from "src/app/business/[businessId]/users/page";
import BusinessAppIQPage from "src/app/business/[businessId]/app-iq/page";
import UpdateBusinessSubscription from "src/app/business/[businessId]/subscriptions/[subscriptionId]/page";

import Invitation from "src/components/Invitation/Invitation";
import PrivateRoute from "src/components/PrivateRoute/PrivateRoute";

import AcceptBusinessInvitation from "src/components/pages/AcceptBusinessInvitation/AcceptBusinessInvitation";
import { Account } from "src/components/pages/Account/Account";
import { Emails } from "src/components/pages/Emails/Emails";
import Activate from "src/components/pages/Activate/Activate";
import Home from "src/components/pages/Home/Home";
import BusinessProfile from "src/components/pages/Profile/BusinessProfile";
import LensLauncher from "src/components/pages/LensLauncher/LensLauncher";
import { Profile } from "src/components/pages/Profile/Profile";
import SignInPage from "src/components/pages/SignInPage/SignInPage";
import SignUpPage from "src/components/pages/SignUpPage/SignUpPage";
import Subscribe from "src/components/pages/Subscribe/Subscribe";
import Support from "src/components/pages/Support/Support";
import Discuss from "src/components/pages/Discuss/Discuss";
import VerifySecondaryEmail from "src/components/pages/VerifySecondaryEmail/VerifySecondaryEmail";
import { NotFound } from "src/components/pages/Error/NotFound";
import { AcceptBusinessChildrenInvitation } from "src/components/pages/AcceptBusinessChildrenInvitation/AcceptBusinessChildrenInvitation";
import { businessChildInvitationTokenParameter } from "src/hooks/useBusinessChildInvitationToken";
import { BillingDetails } from "src/components/pages/Billing/BillingDetails";
import { BusinessBillingDetails } from "./components/pages/Billing/BusinessBillingDetails";
import SSOSignIn from "src/components/pages/SignInPage/SSOSignIn";
import { ConnectedAcounts } from "src/components/pages/Connected-acounts/Connected-acounts";
import { ConfirmChildAttachment } from "src/components/pages/ConfirmChildAttachment/ConfirmChildAttachment";

import BusinessBase from "./components/Business/Base";
import RedirectToFirstBusiness from "src/components/Business/RedirectToFirstBusiness";
import Business from "src/components/Business/Business";
import BusinessAccount from "src/components/Business/Account";
import BusinessFeatureFlagSettings from "src/components/Business/FeatureManagement";
import BusinessParentAndChildSettings from "src/components/Business/AccountHierarchy";
import ManageSeats from "src/components/Business/ManageSeats";

export const Routes = () => {
  useTrackPageView();

  const { invitationKey, activationKey, businessIdInvitationKey, businessChildInvitationToken } = usePassThroughKeys();
  const { token } = useContext(TokenContext);

  const { identifyUser, track } = useAnalytics();

  useEffect(() => {
    if (token !== undefined) {
      // Track changes in user
      identifyUser(token);
    }
  }, [identifyUser, token]);

  return (
    <Switch>
      <Route path="/lens-launcher">
        <LensLauncher />
      </Route>
      <Route
        path={[
          "/signin/realms/:realm?/protocol/openid-connect/auth",
          "/signin/invitation-key/:invitationKey",
          "/signin/activation-key/:activationKey",
          "/signin/business-invitation-key/:businessInvitationKey",
          `/signin/business-child-invitation-token/:${businessChildInvitationTokenParameter}`,
          "/signin",
        ]}
      >
        <SignInPage />
      </Route>
      <Route path="/sso/:handle">
        <SSOSignIn />
      </Route>
      <Route
        path={[
          "/signup/invitation-key/:invitationKey",
          "/signup/activation-key/:activationKey",
          "/signup/business-invitation-key/:businessInvitationKey",
          `/signup/business-child-invitation-token/:${businessChildInvitationTokenParameter}`,
          "/signup",
        ]}
      >
        <SignUpPage />
      </Route>
      <Route path="/verify-secondary-email" exact>
        <VerifySecondaryEmail />
      </Route>
      <Route path="/confirm-child-attachment" exact>
        <ConfirmChildAttachment />
      </Route>
      <PrivateRoute path="/logged-in">
        <Redirect to="/" />
      </PrivateRoute>
      <PrivateRoute path={["/home/activate", "/subscriptions/activate"]} exact>
        <Activate />
      </PrivateRoute>
      <PrivateRoute path="/business-invitation" exact>
        <AcceptBusinessInvitation />
      </PrivateRoute>
      <PrivateRoute path={"/billing-details"} exact>
        <BillingDetails />
      </PrivateRoute>
      <PrivateRoute path="/home" exact>
        {businessIdInvitationKey && <AcceptBusinessInvitation />}
        {businessChildInvitationToken && <AcceptBusinessChildrenInvitation />}
        {activationKey && <Activate />}
        {!activationKey && !businessIdInvitationKey && !businessChildInvitationToken && invitationKey && <Invitation />}
        {!activationKey && !businessIdInvitationKey && !businessChildInvitationToken && !invitationKey && <Home />}
      </PrivateRoute>
      <PrivateRoute path="/business" exact>
        <RedirectToFirstBusiness />
      </PrivateRoute>
      <PrivateRoute path="/business/:businessId/subscriptions/:subscriptionId" exact>
        <UpdateBusinessSubscription />
      </PrivateRoute>
      <Route path={"/business/:businessId"} exact={false}>
        <BusinessBase>
          <Switch>
            <PrivateRoute path="/business/:businessId" exact>
              <Business />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/account" exact>
              <BusinessAccount />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/parent-child-settings" exact>
              <BusinessParentAndChildSettings />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/feature-flag-settings" exact>
              <BusinessFeatureFlagSettings />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/profile" exact>
              <BusinessProfile />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/subscriptions" exact>
              <ManageSeats />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/billing-details" exact>
              <BusinessBillingDetails />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/users" exact={false}>
              <BusinessUsers />
            </PrivateRoute>
            <PrivateRoute path="/business/:businessId/app-iq" exact={false}>
              <BusinessAppIQPage />
            </PrivateRoute>
            <PrivateRoute>
              {({ match }) => {
                return <Redirect to={`/business/${match?.params.businessId}`} />;
              }}
            </PrivateRoute>
          </Switch>
        </BusinessBase>
      </Route>
      <PrivateRoute path="/support">
        <Support />
      </PrivateRoute>
      <PrivateRoute path="/forums">
        <Discuss />
      </PrivateRoute>
      <PrivateRoute path="/home/invitation-key/:invitationKey">
        <Invitation />
      </PrivateRoute>
      <PrivateRoute path={`/home/business-child-invitation-token/:${businessChildInvitationTokenParameter}`}>
        <AcceptBusinessChildrenInvitation />
      </PrivateRoute>
      <Route path={["/subscribe", "/subscribe-personal"]}>
        <Subscribe />
      </Route>
      {/* Legacy path for backward compatibiliy */}
      <Route path="/subscribe-for-teams" exact={false}>
        {({ location }) => {
          track("Legacy path '/subscribe-for-teams'", { location });
          const to = {
            ...location,
            pathname: location.pathname.replace(/^\/subscribe-for-teams/, "/subscribe"),
          };

          return <Redirect to={to} />;
        }}
      </Route>
      <PrivateRoute path="/profile">
        <Profile />
      </PrivateRoute>
      <PrivateRoute path="/account">
        <Account />
      </PrivateRoute>
      <PrivateRoute path="/connected-accounts">
        <ConnectedAcounts />
      </PrivateRoute>
      <PrivateRoute path="/emails">
        <Emails />
      </PrivateRoute>
      <PrivateRoute path="/" exact>
        <Redirect to="/home" />
      </PrivateRoute>
      <Route path="*">
        <NotFound />
      </Route>
    </Switch>
  );
};
